import React, { useState, useEffect } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import notificationService from '../../../services/notificationService';

const NotificationSettings = ({ onChange }) => {
  const [notificationSettings, setNotificationSettings] = useState({
    pushNotifications: true,
    emailNotifications: true,
    notifyMoodChanges: true,
    notifyScheduleReminders: true,
    notifyAIMessages: true,
    notifyPartnerActivity: true,
    notifyCompatibilityAlerts: true,
    notifyEncounterFeedback: false,
    quietHoursEnabled: true,
    quietHoursStart: '22:00',
    quietHoursEnd: '08:00',
    notificationFrequency: 'immediate'
  });

  const [permissionStatus, setPermissionStatus] = useState('default');
  const [scheduledCount, setScheduledCount] = useState(0);
  const [isTestingNotification, setIsTestingNotification] = useState(false);

  // Add this block - declare proactiveSettings state
  const [proactiveSettings, setProactiveSettings] = useState({
    proactiveAnalysisEnabled: false,
    analysisFrequency: 'daily',
    lookAheadDays: 7,
    minCompatibilityThreshold: 75,
    notifyOnNewSuggestions: true
  });

  useEffect(() => {
    // Load settings from notification service
    const settings = notificationService?.loadSettings();
    setNotificationSettings(settings);
    setPermissionStatus(notificationService?.getPermissionStatus());
    setScheduledCount(notificationService?.getScheduledCount());
  }, []);

  const handleToggle = (name) => {
    const newSettings = { ...notificationSettings, [name]: !notificationSettings?.[name] };
    setNotificationSettings(newSettings);
    notificationService?.updateSettings(newSettings);
    onChange?.();
  };

  const handleSelectChange = (name, value) => {
    const newSettings = { ...notificationSettings, [name]: value };
    setNotificationSettings(newSettings);
    notificationService?.updateSettings(newSettings);
    onChange?.();
  };

  const handleRequestPermission = async () => {
    const granted = await notificationService?.requestPermission();
    setPermissionStatus(granted ? 'granted' : 'denied');
  };

  const handleTestNotification = async () => {
    setIsTestingNotification(true);
    await notificationService?.sendNotification('ai_suggestion', {
      message: 'This is a test notification. Your push alerts are working correctly!'
    });
    setTimeout(() => setIsTestingNotification(false), 2000);
  };

  // Add this block - declare proactive handlers
  const handleProactiveToggle = (name) => {
    const newSettings = { ...proactiveSettings, [name]: !proactiveSettings?.[name] };
    setProactiveSettings(newSettings);
    // Could integrate with notificationService if needed
    onChange?.();
  };

  const handleProactiveSelectChange = (name, value) => {
    const newSettings = { ...proactiveSettings, [name]: value };
    setProactiveSettings(newSettings);
    // Could integrate with notificationService if needed
    onChange?.();
  };

  const handleForceAnalysis = async () => {
    // Trigger immediate analysis
    // Could integrate with notificationService or analytics service
    console.log('Force analysis triggered');
  };

  const frequencyOptions = [
    { value: 'immediate', label: 'Immediate' },
    { value: 'hourly', label: 'Hourly Digest' },
    { value: 'daily', label: 'Daily Summary' },
    { value: 'off', label: 'Off' }
  ];

  const timeOptions = [
    { value: '00:00', label: '12:00 AM' },
    { value: '01:00', label: '1:00 AM' },
    { value: '02:00', label: '2:00 AM' },
    { value: '06:00', label: '6:00 AM' },
    { value: '07:00', label: '7:00 AM' },
    { value: '08:00', label: '8:00 AM' },
    { value: '09:00', label: '9:00 AM' },
    { value: '10:00', label: '10:00 AM' },
    { value: '20:00', label: '8:00 PM' },
    { value: '21:00', label: '9:00 PM' },
    { value: '22:00', label: '10:00 PM' },
    { value: '23:00', label: '11:00 PM' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Notification Channels
        </h3>
        <div className="space-y-4">
          <div>
            <Checkbox
              checked={notificationSettings?.pushNotifications}
              onChange={() => handleToggle('pushNotifications')}
              label="Push Notifications"
              description="Receive notifications on your device"
            />
            {permissionStatus !== 'granted' && notificationSettings?.pushNotifications && (
              <div className="mt-3 ml-6 p-3 bg-warning/10 border border-warning/30 rounded-md">
                <div className="flex items-start space-x-2">
                  <Icon name="AlertCircle" size={18} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-foreground mb-2">
                      {permissionStatus === 'denied' ?'Push notifications are blocked. Please enable them in your browser settings.' :'Push notifications require permission to work.'}
                    </p>
                    {permissionStatus === 'default' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRequestPermission}
                      >
                        Enable Push Notifications
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <Checkbox
            checked={notificationSettings?.emailNotifications}
            onChange={() => handleToggle('emailNotifications')}
            label="Email Notifications"
            description="Receive notifications via email"
          />
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Notification Types
        </h3>
        <div className="space-y-4">
          <Checkbox
            checked={notificationSettings?.notifyMoodChanges}
            onChange={() => handleToggle('notifyMoodChanges')}
            label="Mood Changes"
            description="Get notified when your partner logs a mood entry"
          />
          <Checkbox
            checked={notificationSettings?.notifyScheduleReminders}
            onChange={() => handleToggle('notifyScheduleReminders')}
            label="Schedule Reminders"
            description="Receive reminders for upcoming scheduled intimate moments"
          />
          <Checkbox
            checked={notificationSettings?.notifyAIMessages}
            onChange={() => handleToggle('notifyAIMessages')}
            label="AI Messages"
            description="Get notified when AI has mediated a message from your partner"
          />
          <Checkbox
            checked={notificationSettings?.notifyPartnerActivity}
            onChange={() => handleToggle('notifyPartnerActivity')}
            label="Partner Activity"
            description="Receive updates about your partner's app activity"
          />
          <Checkbox
            checked={notificationSettings?.notifyCompatibilityAlerts}
            onChange={() => handleToggle('notifyCompatibilityAlerts')}
            label="Compatibility Alerts"
            description="Get notified about high compatibility windows and optimal timing"
          />
          <Checkbox
            checked={notificationSettings?.notifyEncounterFeedback}
            onChange={() => handleToggle('notifyEncounterFeedback')}
            label="Encounter Feedback"
            description="Receive reminders to provide feedback after intimate encounters"
          />
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Notification Frequency
        </h3>
        <Select
          label="Delivery Frequency"
          value={notificationSettings?.notificationFrequency}
          onChange={(value) => handleSelectChange('notificationFrequency', value)}
          options={frequencyOptions}
          description="Choose how often you want to receive notifications"
        />
      </div>

      <div className="border-t border-border pt-6">
        <div className="flex items-start space-x-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Moon" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Quiet Hours
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Set a time period when you don't want to receive notifications
            </p>
          </div>
        </div>

        <div className="space-y-4 ml-0 md:ml-13">
          <Checkbox
            checked={notificationSettings?.quietHoursEnabled}
            onChange={() => handleToggle('quietHoursEnabled')}
            label="Enable Quiet Hours"
            description="Pause notifications during specified hours"
          />

          {notificationSettings?.quietHoursEnabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
              <Select
                label="Start Time"
                value={notificationSettings?.quietHoursStart}
                onChange={(value) => handleSelectChange('quietHoursStart', value)}
                options={timeOptions}
              />
              <Select
                label="End Time"
                value={notificationSettings?.quietHoursEnd}
                onChange={(value) => handleSelectChange('quietHoursEnd', value)}
                options={timeOptions}
              />
            </div>
          )}

          {notificationSettings?.quietHoursEnabled && scheduledCount > 0 && (
            <div className="pl-6 p-3 bg-primary/5 border border-primary/20 rounded-md">
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} color="var(--color-primary)" />
                <p className="text-sm text-foreground">
                  <span className="font-medium">{scheduledCount}</span> notification{scheduledCount !== 1 ? 's' : ''} scheduled for after quiet hours
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {permissionStatus === 'granted' && (
        <div className="border-t border-border pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-heading font-semibold text-foreground">
                Test Notifications
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Send a test notification to verify your settings
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleTestNotification}
              disabled={isTestingNotification}
              iconName={isTestingNotification ? 'Loader2' : 'Bell'}
              iconPosition="left"
            >
              {isTestingNotification ? 'Sending...' : 'Send Test'}
            </Button>
          </div>
        </div>
      )}

      <div className="border-t border-border pt-6">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="Sparkles" size={20} color="var(--color-accent)" />
          <h3 className="text-lg font-heading font-semibold text-foreground">
            Proactive AI Analysis
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Claude analyzes your mood patterns, availability history, and preferences to proactively suggest optimal encounter windows before you even open the app.
        </p>
        
        <div className="space-y-4">
          <Checkbox
            checked={proactiveSettings?.proactiveAnalysisEnabled}
            onChange={() => handleProactiveToggle('proactiveAnalysisEnabled')}
            label="Enable Proactive Analysis"
            description="Automatically analyze patterns and generate suggestions"
          />

          {proactiveSettings?.proactiveAnalysisEnabled && (
            <>
              <div className="ml-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Analysis Frequency
                  </label>
                  <Select
                    value={proactiveSettings?.analysisFrequency}
                    onChange={(value) => handleProactiveSelectChange('analysisFrequency', value)}
                    options={[
                      { value: 'hourly', label: 'Every Hour' },
                      { value: 'daily', label: 'Daily' },
                      { value: 'weekly', label: 'Weekly' }
                    ]}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    How often Claude should analyze your patterns
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Look Ahead Period
                  </label>
                  <Select
                    value={proactiveSettings?.lookAheadDays}
                    onChange={(value) => handleProactiveSelectChange('lookAheadDays', parseInt(value))}
                    options={[
                      { value: '3', label: '3 Days' },
                      { value: '7', label: '7 Days' },
                      { value: '14', label: '14 Days' },
                      { value: '30', label: '30 Days' }
                    ]}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    How far ahead to generate suggestions
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Minimum Compatibility Score
                  </label>
                  <Select
                    value={proactiveSettings?.minCompatibilityThreshold}
                    onChange={(value) => handleProactiveSelectChange('minCompatibilityThreshold', parseInt(value))}
                    options={[
                      { value: '60', label: '60% - Show More Suggestions' },
                      { value: '70', label: '70% - Balanced' },
                      { value: '75', label: '75% - Recommended' },
                      { value: '80', label: '80% - High Quality' },
                      { value: '90', label: '90% - Only Best Matches' }
                    ]}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Only show suggestions above this compatibility score
                  </p>
                </div>

                <Checkbox
                  checked={proactiveSettings?.notifyOnNewSuggestions}
                  onChange={() => handleProactiveToggle('notifyOnNewSuggestions')}
                  label="Notify on New Suggestions"
                  description="Receive push notifications when new optimal windows are detected"
                />

                <div className="pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="RefreshCw"
                    iconPosition="left"
                    onClick={handleForceAnalysis}
                    fullWidth
                  >
                    Run Analysis Now
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    Manually trigger pattern analysis to get fresh suggestions
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;